import { captureEvent } from "@/utils/posthog";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

export function useClerkComponentTracking(
  componentType: "sign_in" | "sign_up"
) {
  const location = useLocation();
  const cleanupRef = useRef<(() => void) | null>(null);
  const verificationTrackedRef = useRef(false);
  const previousHashRef = useRef(location.hash);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let mutationObserver: MutationObserver | null = null;

    const isOnVerificationStep = () => {
      return (
        location.hash.includes("factor-one") ||
        location.hash.includes("verify-email-address")
      );
    };

    const trackVerificationComplete = () => {
      if (!verificationTrackedRef.current) {
        captureEvent(`email_auth_code_verify`);
        verificationTrackedRef.current = true;
      }
    };

    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const button = target.closest("button");

      if (!button) return;

      const buttonText = button.textContent?.toLowerCase() || "";

      // Track OAuth providers
      if (buttonText.includes("google")) {
        captureEvent(`${componentType}_google_click`);
        captureEvent(`${componentType}_click_all`);
      } else if (buttonText.includes("microsoft")) {
        captureEvent(`${componentType}_microsoft_click`);
        captureEvent(`${componentType}_click_all`);
      } else if (buttonText.includes("apple")) {
        captureEvent(`${componentType}_apple_click`);
        captureEvent(`${componentType}_click_all`);
      } else if (buttonText.includes("continue") && isOnVerificationStep()) {
        // Track verification via button click
        trackVerificationComplete();
      } else if (buttonText.includes("continue")) {
        captureEvent(`email_${componentType}`);
        captureEvent(`${componentType}_click_all`);
      }
    };

    // Wait a bit for Clerk to render
    timeoutId = setTimeout(() => {
      const clerkRoot = document.querySelector(".cl-rootBox");
      if (!clerkRoot) return;

      // Add click listener
      clerkRoot.addEventListener("click", handleClick, true);

      // Watch for automatic verification (when code is auto-submitted)
      if (isOnVerificationStep()) {
        mutationObserver = new MutationObserver(() => {
          // Check if we've moved away from verification step
          const verificationInput = document.querySelector(
            'input[name="code"], input[inputmode="numeric"]'
          );
          const hasVerificationForm = !!verificationInput;

          // If verification input disappeared or hash changed, verification likely completed
          if (!hasVerificationForm && !verificationTrackedRef.current) {
            trackVerificationComplete();
          }
        });

        // Observe the Clerk root for changes
        mutationObserver.observe(clerkRoot, {
          childList: true,
          subtree: true,
        });
      }

      // Store cleanup function in ref
      cleanupRef.current = () => {
        clerkRoot.removeEventListener("click", handleClick, true);
        mutationObserver?.disconnect();
      };
    }, 500);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);

      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [componentType, location.hash]);

  // Track when hash changes away from verification step
  useEffect(() => {
    const wasOnVerificationStep =
      previousHashRef.current.includes("factor-one") ||
      previousHashRef.current.includes("verify-email-address");
    const isStillOnVerificationStep =
      location.hash.includes("factor-one") ||
      location.hash.includes("verify-email-address");

    // If we were on verification and now we're not, verification completed
    if (
      wasOnVerificationStep &&
      !isStillOnVerificationStep &&
      !verificationTrackedRef.current
    ) {
      captureEvent(`email_auth_code_verify`);
      verificationTrackedRef.current = true;
    }

    // If we're on a new verification step, reset the tracking
    if (isStillOnVerificationStep && !wasOnVerificationStep) {
      verificationTrackedRef.current = false;
    }

    previousHashRef.current = location.hash;
  }, [location.hash]);
}
